import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Divider, Input, Textarea, TextInput } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import type { ReactNode } from "react";
import { notEmptyMessage } from "../../../helpers/formHelper";
import type { Occupation } from "../../../models/enums/occupation";
import { ReportType } from "../../../models/enums/reportType";
import { Role } from "../../../models/enums/role";
import type { Group } from "../../../models/group";
import { appendUUID } from "../../../models/IDElement";
import { type Aspect, emptyAspect, type ProbationConfiguration } from "../../../models/reportConfigs/probationConfiguration";
import type { YearContext } from "../../../models/yearContext";
import { AccessConfigForm, type AccessConfigFormType } from "../../accessConfig/AccessConfigForm";
import { SaveButton } from "../../button/SaveButton";
import { emailTemplateValidation } from "../../email/EmailEditorForm";
import { ManagableList } from "../../sortable/ManagableList";
import { BaseConfigForm, type BaseConfigFormType } from "./BaseConfigForm";

type ProbationFormProps = {
  probationConfig?: ProbationConfiguration;
  saveButtonLabel: string;
  yearContexts: YearContext[];
  groups: Group[];
  onSubmit: (values: RequestPayload) => void;
  alert?: ReactNode;
};

type ProbationFormType = AccessConfigFormType &
  BaseConfigFormType & {
    report_type: ReportType;
    data: {
      aspects: Aspect[];
    };
  };

type FormErrors = {
  title?: string[];
  data?: string[];
  report_type?: string[];
};

export function ProbationConfigForm({ alert, probationConfig, saveButtonLabel, yearContexts, groups, onSubmit }: ProbationFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm<ProbationFormType>({
    mode: "uncontrolled",
    initialValues: {
      report_type: probationConfig?.report_type ?? ReportType.PROP,
      title: probationConfig?.title,
      group_id: probationConfig?.group?.id.toString(),
      year_context_id: probationConfig?.year_context?.id.toString(),
      occupation: probationConfig?.occupation,
      document_readable: probationConfig?.document_readable ?? [Role.APPRENTICE, Role.APPRENTICE_SUPERVISOR, Role.PARENT],
      document_signable: probationConfig?.document_signable ?? [Role.APPRENTICE],
      email_template: probationConfig?.email_template,
      send_mail: probationConfig?.send_mail ?? true,
      subject: probationConfig?.subject,
      data: {
        aspects: probationConfig?.data.aspects ?? [appendUUID(emptyAspect)],
      },
    },
    validate: {
      title: isNotEmpty(notEmptyMessage),
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
      data: {
        aspects: {
          title: isNotEmpty(notEmptyMessage),
        },
      },
    },
  });

  form.watch("year_context_id", ({ value }) => reloadGroups(value, form.getValues().occupation));
  form.watch("occupation", ({ value }) => reloadGroups(form.getValues().year_context_id, value));

  const reloadGroups = (yearContextId?: string, occupation?: Occupation) =>
    router.reload({
      only: ["groups"],
      data: { selected_year_context_id: yearContextId, selected_occupation: occupation },
    });

  const renderAspectInput = (index: number) => (
    <>
      <TextInput
        placeholder="Titel"
        flex={3}
        key={form.key(`data.aspects.${index}.title`)}
        {...form.getInputProps(`data.aspects.${index}.title`)}
      />
      <Textarea
        placeholder="Beschreibung"
        resize="vertical"
        minRows={1}
        flex={5}
        key={form.key(`data.aspects.${index}.description`)}
        {...form.getInputProps(`data.aspects.${index}.description`)}
      />
      <Checkbox
        flex={1}
        label="Beurteilt"
        key={form.key(`data.aspects.${index}.graded`)}
        {...form.getInputProps(`data.aspects.${index}.graded`, { type: "checkbox" })}
      />
    </>
  );

  const aspectLabels = (
    <>
      <Input.Wrapper flex={3} label="Titel" withAsterisk />
      <Input.Wrapper flex={5} label="Beschreibung" />
      <Input.Wrapper flex={1} />
    </>
  );

  return (
    <BaseConfigForm
      form={form}
      groups={groups}
      yearContexts={yearContexts}
      errors={errors}
      alert={alert}
      saveButtonLabel={saveButtonLabel}
      onSubmit={onSubmit}
    >
      <ManagableList
        defaultProps={emptyAspect}
        onAdd={(asp: Aspect) => form.insertListItem("data.aspects", asp)}
        maw="60rem"
        label="Aspekte der Beurteilung"
        reorderListItem={(from, to) => form.reorderListItem("data.aspects", { from, to })}
        itemRender={renderAspectInput}
        values={form.values.data.aspects}
        onDelete={(index: number) => form.removeListItem("data.aspects", index)}
        detailLabels={aspectLabels}
      />
      <AccessConfigForm form={form} />
      <Divider />
      <SaveButton label={saveButtonLabel} />
    </BaseConfigForm>
  );
}
